/**
 * ONE VERSION of a release, filling the right of the release page —
 * 10.2.0.27, selected in the rail beside it.
 *
 * A version is the thing an appliance reports and the thing a customer
 * installs, so it is where everything people come for hangs:
 *
 * - its **downloads** — the release-notes document and the Disk 1 / Disk 2
 *   installer images, three tiles side by side, each with the audited
 *   Download button (`ReleaseArtifactTiles`: a POST that portal-api decides
 *   AND records, never a stored link);
 * - its **change log**, rendered as Markdown;
 * - the **documentation** that ships with it — Documentation-section documents
 *   pinned to the version, added from here by a documentation author;
 * - its **trainings**, below the documentation — training PDFs and videos
 *   (`purpose: "training"`) that resellers read on their Trainings page and
 *   customers never see (`ReleaseTrainings`);
 * - and its **download journal**, behind a button: who asked for which file
 *   and whether they got it (`ReleaseDownloadJournalDialog`).
 *
 * Curation — attach a notes document, link a disk image, retire, remove —
 * is **Manage files** (`ReleaseArtifactsDialog`), for `admin:users`, beside
 * Edit / Publish / Delete. A read-only holder of the page gets everything
 * else, download buttons included: a refusal that lands in the journal is
 * more useful than a control that quietly is not there.
 */
import { Box, Button, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import {
  BookOpen,
  FileText,
  GraduationCap,
  HardDrive,
  HardDriveDownload,
  Pencil,
  Rocket,
  ScrollText,
  Trash2,
} from "lucide-react";
import type { JSX } from "react";

import { Markdown } from "../../../pages/documentation/markdown.js";
import { ReleaseArtifactTiles } from "./ReleaseArtifactTiles.js";
import { ReleaseDocuments } from "./ReleaseDocuments.js";
import { ReleaseTrainings } from "./ReleaseTrainings.js";
import { RELEASE_STATUS_COLOR, statusLabel } from "./release-status.js";
import type { ReleaseDTO } from "./releases-api.js";

interface Props {
  release: ReleaseDTO;
  canEdit: boolean;
  /** May add documents — the Documentation authoring tiers, not `admin:users`. */
  canAuthorDocs: boolean;
  onOpenJournal: () => void;
  onManageFiles: () => void;
  onEdit: () => void;
  onPublish: () => void;
  onDelete: () => void;
}

export function VersionDetail({
  release,
  canEdit,
  canAuthorDocs,
  onOpenJournal,
  onManageFiles,
  onEdit,
  onPublish,
  onDelete,
}: Props): JSX.Element {
  const notes = release.release_notes?.trim() ?? "";

  return (
    <Box
      component="article"
      data-testid={`version-detail-${release.version}`}
      aria-label={`Version ${release.version}`}
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        bgcolor: "background.paper",
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
          px: 2.5,
          py: 1.75,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography
              component="h4"
              sx={{
                fontFamily: (t) => t.typography.h6.fontFamily,
                fontSize: 20,
                fontWeight: 700,
                letterSpacing: "-0.01em",
                lineHeight: 1.2,
              }}
            >
              {release.version}
            </Typography>
            <Chip
              size="small"
              variant={release.status === "released" ? "filled" : "outlined"}
              color={RELEASE_STATUS_COLOR[release.status] ?? "default"}
              label={statusLabel(release.status)}
              sx={{ height: 22 }}
            />
          </Box>
          <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.25 }}>
            {release.release_date ?? "No date yet"}
            {release.description ? ` · ${release.description}` : ""}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0, flexWrap: "wrap" }}>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<ScrollText size={15} />}
            aria-label={`Download journal for ${release.version}`}
            onClick={onOpenJournal}
          >
            Download journal
          </Button>
          {canEdit && (
            <>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                startIcon={<HardDriveDownload size={15} />}
                aria-label={`Manage files for ${release.version}`}
                onClick={onManageFiles}
              >
                Manage files
              </Button>
              <IconButton size="small" color="inherit" aria-label={`Edit ${release.version}`} onClick={onEdit}>
                <Pencil size={16} />
              </IconButton>
              {!release.released && (
                <Tooltip title="Publish this version">
                  <IconButton
                    size="small"
                    color="inherit"
                    aria-label={`Publish ${release.version}`}
                    onClick={onPublish}
                  >
                    <Rocket size={16} />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton
                size="small"
                aria-label={`Delete ${release.version}`}
                onClick={onDelete}
                sx={{ color: "error.main" }}
              >
                <Trash2 size={16} />
              </IconButton>
            </>
          )}
        </Box>
      </Box>

      <Box sx={{ px: 2.5, py: 2.5, display: "grid", gap: 3 }}>
        <Box component="section" aria-label={`Downloads for ${release.version}`} sx={{ minWidth: 0 }}>
          <Typography sx={sectionHeadSx}>
            <HardDrive size={13} style={{ verticalAlign: "-2px", marginRight: 6 }} />
            Downloads
          </Typography>
          <ReleaseArtifactTiles release={release} columns />
        </Box>

        <Box
          sx={{
            display: "grid",
            gap: 3,
            gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 3fr) minmax(300px, 2fr)" },
          }}
        >
          <Box component="section" aria-label={`Change log for ${release.version}`} sx={{ minWidth: 0 }}>
            <Typography sx={sectionHeadSx}>
              <FileText size={13} style={{ verticalAlign: "-2px", marginRight: 6 }} />
              Change log
            </Typography>
            {notes ? (
              <Box sx={{ fontSize: 14, "& h1, & h2, & h3": { fontSize: 15, mt: 1.5, mb: 0.5 } }}>
                <Markdown source={notes} />
              </Box>
            ) : (
              <Typography sx={{ fontSize: 13, color: "text.disabled" }}>
                No change log written for this version yet.
              </Typography>
            )}
          </Box>

          <Box component="section" aria-label={`Documentation for ${release.version}`} sx={{ minWidth: 0 }}>
            <Typography sx={sectionHeadSx}>
              <BookOpen size={13} style={{ verticalAlign: "-2px", marginRight: 6 }} />
              Documentation
            </Typography>
            <ReleaseDocuments release={release} canAuthorDocs={canAuthorDocs} />
          </Box>
        </Box>

        <Box component="section" aria-label={`Trainings for ${release.version}`} sx={{ minWidth: 0 }}>
          <Typography sx={sectionHeadSx}>
            <GraduationCap size={13} style={{ verticalAlign: "-2px", marginRight: 6 }} />
            Trainings
          </Typography>
          <ReleaseTrainings release={release} canAuthorDocs={canAuthorDocs} />
        </Box>
      </Box>
    </Box>
  );
}

const sectionHeadSx = {
  mb: 1,
  fontSize: 11,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "text.disabled",
} as const;
